import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import { AuthConfirmationButton } from "./AuthConfirmationButton";

/**
 * Tool parameters for showing authentication confirmation buttons
 */
export const AuthConfirmationButtonParamsSchema = z.object({
  scenario: z
    .enum(["single_auth", "multiple_auth"])
    .describe(
      "The authentication scenario: 'single_auth' for confirming one auth, 'multiple_auth' for selecting from multiple",
    ),
  app_name: z
    .string()
    .describe(
      "The name of the app requiring authentication (e.g., 'Slack', 'Gmail')",
    ),
  auth_details: z
    .object({
      auth_id: z.string().describe("Authentication ID"),
      auth_label: z.string().describe("Authentication label/name"),
      status: z.string().describe("Authentication status"),
    })
    .optional()
    .describe("Details for single auth confirmation scenario"),
  suggested_auth: z
    .object({
      auth_id: z.string().describe("Suggested authentication ID"),
      auth_label: z.string().describe("Suggested authentication label/name"),
      status: z.string().describe("Suggested authentication status"),
    })
    .optional()
    .describe("Suggested auth details for multiple auth scenario"),
});

export type AuthConfirmationButtonResult = {
  success: boolean;
  message: string;
};

/**
 * Authentication confirmation button tool for Canvas AI toolkit
 * Shows appropriate buttons for authentication confirmation scenarios
 * Prevents UX conflict between continue button and typed auth confirmation
 */
export const authConfirmationButtonAtom = atom<
  JotaiTool<z.infer<typeof AuthConfirmationButtonParamsSchema>>
>(() => ({
  name: "auth_confirmation_button",
  description: `🚨 CRITICAL: BUTTON TOOL - ENDS RESPONSE IMMEDIATELY 🚨

Show authentication confirmation buttons when get_app_auth returns REQUIRES_USER_INPUT: true.

MANDATORY BEHAVIOR:
- This tool shows buttons and MUST be the LAST tool call in any response
- After calling this tool, DO NOT output any additional text or call any other tools
- The response MUST end immediately to prevent UX conflicts

This tool provides proper button interfaces instead of requiring typed confirmations.

Use cases:
- single_auth: Show "Use this authentication" button for single auth confirmation
- multiple_auth: Show "Use suggested", "Select different", and "Create new" buttons

The buttons send appropriate messages that match get_app_auth's expected responses.

CRITICAL RULES:
- Use this INSTEAD of continue_button when authentication confirmation is required
- NEVER use with other button tools in the same response
- RESPONSE MUST END IMMEDIATELY after this tool call`,
  parameters: AuthConfirmationButtonParamsSchema,
  component: AuthConfirmationButton,
  execute: atom(null, async () => {
    const result: AuthConfirmationButtonResult = {
      success: true,
      message: "Showing authentication confirmation buttons for user selection",
    };

    return {
      content: [
        {
          type: "text",
          text: result.message,
        },
      ],
      // Signal that user interaction is required via button selection
      waitingForUser: true,
    };
  }),
}));
